/**
 * Almost Friday Pod - Brand-specific JavaScript
 * YouTube-style UI customizations
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Only run on AF Pod brand pages
    if (!document.documentElement.hasAttribute('data-brand') ||
        document.documentElement.getAttribute('data-brand') !== 'af-pod') {
      return;
    }

    // Initialize custom sort buttons
    initCustomSort();
    
    // Add YouTube-style menu icons to product cards
    addCardMenuIcons();
    
    // Add YouTube subscribe button to toolbar
    addSubscribeButton();
  }

  /**
   * Replace the sort dropdown with YouTube-style buttons
   */
  function initCustomSort() {
    const sortSelect = document.querySelector('#products-sort-by');
    if (!sortSelect) return;

    const selectElement = sortSelect.querySelector('select') ||
                         sortSelect.querySelector('.custom-select__btn');
    if (!selectElement) return;

    // Get all sort options
    const options = Array.from(sortSelect.querySelectorAll('.custom-select__option'));
    if (options.length === 0) return;

    // Hide the original select
    sortSelect.style.display = 'none';

    // Create custom button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'af-pod-sort-buttons';

    // Get currently selected value
    const getSelectedValue = () => {
      const selected = sortSelect.querySelector('[aria-selected="true"]');
      return selected ? selected.dataset.value : 'manual';
    };

    let currentValue = getSelectedValue();

    // Options to exclude
    const excludedOptions = [
      'title-descending',  // Alphabetically, Z-A
      'price-descending'   // Price, high to low
    ];

    // Label mapping for custom text
    const labelMap = {
      'created-descending': 'Newest',
      'created-ascending': 'Oldest',
      'title-ascending': 'Alphabetical',
      'price-ascending': 'Affordability'
    };

    // Helper to convert text to title case (exclude "to")
    const toTitleCase = (str) => {
      return str.replace(/\w\S*/g, (txt) => {
        const lower = txt.toLowerCase();
        if (lower === 'to') return lower;
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    };

    // Create buttons for each option
    options.forEach(option => {
      const value = option.dataset.value;

      // Skip excluded options
      if (excludedOptions.includes(value)) {
        return;
      }

      let label = option.querySelector('span')?.textContent || option.textContent;
      label = label.trim();

      // Use custom label if exists, otherwise title case
      label = labelMap[value] || toTitleCase(label);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'af-pod-sort-btn' + (value === currentValue ? ' active' : '');
      button.dataset.value = value;
      button.textContent = label;

      // Click handler
      button.addEventListener('click', () => {
        // Find the option in the original custom-select and trigger it
        const targetOption = Array.from(options).find(opt => opt.dataset.value === value);
        
        if (targetOption) {
          // Get the custom-select element
          const customSelect = sortSelect.closest('custom-select');
          
          if (customSelect && customSelect.selectOption) {
            // Use the custom-select's built-in selectOption method
            customSelect.selectOption(targetOption);
          } else {
            // Fallback: manually trigger the option click
            targetOption.click();
          }
        }

        // Update button states
        currentValue = value;
        updateButtonStates();
      });

      buttonContainer.appendChild(button);
    });

    // Function to update button states
    function updateButtonStates() {
      buttonContainer.querySelectorAll('.af-pod-sort-btn').forEach(btn => {
        const isActive = btn.dataset.value === currentValue;
        if (isActive) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
    }

    // Insert the button container after the original select
    sortSelect.parentNode.insertBefore(buttonContainer, sortSelect.nextSibling);
  }

  /**
   * Add YouTube-style three-dot menu icons to product card titles
   */
  function addCardMenuIcons() {
    const cardTitles = document.querySelectorAll('.card__title');
    
    cardTitles.forEach(titleElement => {
      // Skip if already has icon
      if (titleElement.querySelector('.af-pod-menu-icon')) {
        return;
      }

      // Create icon wrapper with more aggressive event handling
      const iconWrapper = document.createElement('button');
      iconWrapper.type = 'button';
      iconWrapper.className = 'af-pod-menu-icon';
      iconWrapper.setAttribute('aria-label', 'Quick add to cart');
      iconWrapper.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" focusable="false" aria-hidden="true">
          <path d="M12 4a2 2 0 100 4 2 2 0 000-4Zm0 6a2 2 0 100 4 2 2 0 000-4Zm0 6a2 2 0 100 4 2 2 0 000-4Z"></path>
        </svg>
      `;

      // Add click handler with immediate propagation stop
      iconWrapper.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Find the closest card element
        const card = titleElement.closest('.card--product');
        if (card) {
          // Find the quick-add button within this card
          const quickAddBtn = card.querySelector('.js-quick-add');
          if (quickAddBtn) {
            quickAddBtn.click();
          }
        }
        
        return false;
      }, true); // Use capture phase

      // Also prevent mousedown to be extra safe
      iconWrapper.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);

      // Append icon to title
      titleElement.appendChild(iconWrapper);
    });
  }

  /**
   * Add YouTube-style Subscribe button to toolbar
   */
  function addSubscribeButton() {
    const toolbar = document.querySelector('.products-toolbar');
    if (!toolbar) return;

    // Skip if already exists
    if (toolbar.querySelector('.af-pod-subscribe-btn')) {
      return;
    }

    // Create subscribe button
    const subscribeBtn = document.createElement('a');
    subscribeBtn.href = 'https://youtube.com/@almostfridaypod?si=xiKA56ZLXi5YLc4r';
    subscribeBtn.target = '_blank';
    subscribeBtn.rel = 'noopener noreferrer';
    subscribeBtn.className = 'btn af-pod-subscribe-btn';
    subscribeBtn.innerHTML = `
      <svg viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" focusable="false">
        <g>
          <path d="M10,16.5V7.5L16,12M20,4.4C19.4,4.2 15.7,4 12,4C8.3,4 4.6,4.19 4,4.38C2.44,4.9 2,8.4 2,12C2,15.59 2.44,19.1 4,19.61C4.6,19.81 8.3,20 12,20C15.7,20 19.4,19.81 20,19.61C21.56,19.1 22,15.59 22,12C22,8.4 21.56,4.91 20,4.4Z"></path>
        </g>
      </svg>
      <span>Subscribe</span>
    `;

    // Append to toolbar
    toolbar.appendChild(subscribeBtn);
  }

})();



